import React, {useEffect, useRef, useState} from 'react';
import Transition from 'utils/Transition';
import { useFiltersSidebarCollapsed } from "../hooks/useFiltersSidebarCollapsed";
import { useLatestRequest } from "../hooks/useLatestRequest";
import SelectedActionsButton from "../components/StocksLayout/SelectedActionsButton";
import { useSelectedActions } from "../hooks/useSelectedActions";

import { useAppDispatch, useAppSelector } from 'redux/hooks';
import reducer, {
    setIsNeedUpdateData,
    setRowData,
    setIdFilter,
    setSkuFilter,
    setBarcodeFilter,
    setSizeFilter,
    setNameFilter,
    setBrandFilter,
    setNotesFilter,
    editRowDataElement,
} from "../redux/stocksExport/reducer";
import Actions from "../components/Tables/Actions/Actions";
import StocksFilterSidebar, { FilterSidebarSection } from '../components/StocksLayout/StocksFilterSidebar';
import Table from "../components/Tables/StocksFBSTable/Table";
import store from "../redux/store";
import {IFilterOption, ITableSettingsLegacy} from "../constants/Table/interfaces";
import warehousesService from "../api/services/warehousesService";
import sellerProductsService, {IProductsRequest} from "../api/services/sellerProductsService";
import {useTranslation} from "react-i18next";
import { warehouseTypeLabels } from 'components/Tables/WarehouseTable/parse-warehouse-type';
import Icon from "../components/commons/Icons";
import rowsPerPageBasedOnViewport from "../utils/PaginationHelper";
import {Link} from "react-router-dom";
import url from "../utils/UrlHelper";
import OpenStocksExportForm from 'components/Tables/StocksExportTable/Actions/OpenStocksExportForm';
import {SlidingContainer} from "../components/SlidingContainer/SlidingContainer";
import FormStocksExport from 'components/Forms/FormStocksExport/FormStocksExport';
import stocksExport, {setIsStocksExportFormModalOpen} from "../redux/stocksExport/reducer";
import stocksExportService from "../api/services/stocksExportService";
import {IStocksExportRuleForWarehouse} from "../constants/StocksExport/IStocksExportRuleForWarehouse";
import { ReactComponent as FilterFunnelIcon } from '../assets/icons/new/UI/FilterFunnel.svg';
import { ReactComponent as HideMenuIcon } from '../assets/icons/new/UI/HideMenu.svg';
import ExportStocksToWarehouses from "../components/Tables/StocksExportTable/Actions/ExportStocksToWarehouses";
import {v4 as uuidv4} from "uuid";
import {IWarehouse} from "../constants/Warehouses/IWarehouse";
import {AxiosPromise} from "axios";
import {WarehouseType} from "../constants/Warehouses/WarehouseType";
import Tabs from "../components/Tables/StocksFBSTable/Actions/Tabs";
import RecalcAndExportStocksToWarehouses
    from "../components/Tables/StocksExportTable/Actions/RecalcAndExportStocksToWarehouses";

const StocksExport: React.FC = () => {
    const { t } = useTranslation();

    const { sellerId } = useAppSelector(({ sellers }) => ({
        sellerId: sellers.currentSeller.info.data?.id,
    }))

    const isNeedUpdateData = useAppSelector((state) => state.stocksExport.isNeedUpdateData);

    const dispatch = useAppDispatch();

    const useHasChanged = (val: any) => {
        const prevVal = usePrevious(val)
        return prevVal !== val
    }

    const usePrevious = (value: any) => {
        const ref = useRef();
        useEffect(() => {
            ref.current = value;
        });
        return ref.current;
    }

    function disableIsNeedUpdateData (bounce: number) {
        setTimeout(() => {
            store.dispatch(setIsNeedUpdateData(false))
        }, bounce);
    }

    const [columns, setColumns] = React.useState<any[]>([
        { Header: '', accessor: 'attributes.photo', id: "photo", disableSortBy: true, minWidth: 42, width: 42, maxWidth: 42, sticky: 'left' },
        { Header: 'Артикул', accessor: 'attributes.sku', id: "sku", minWidth: 140, maxWidth: 400 },
        { Header: 'ID', accessor: 'attributes.id', id: "id", minWidth: 100, width: 100, maxWidth: 100},
        { Header: 'Штрих-код', accessor: 'attributes.barcode', id: "barcode", minWidth: 125, width: 125, maxWidth: 125},
        { Header: 'Размер', accessor: 'attributes.size', id: "size", minWidth: 125, width: 125, maxWidth: 125},
        { Header: 'Бренд', accessor: 'attributes.brand', id: "brand", minWidth: 125, width: 125, maxWidth: 125},
        { Header: 'Заметки', accessor: 'attributes.notes', id: "notes", minWidth: 125, width: 125, maxWidth: 125},
        { Header: 'Наименование', accessor: 'attributes.name', id: "name", minWidth: 200, width: "100%", maxWidth: "100%"},
        { Header: 'К продаже', accessor: 'attributes.availableForPurchase', id: "availableForPurchase", minWidth: 100, width: 100, maxWidth: 100 },
        // { Header: 'Не распределено', accessor: 'attributes.notDistributed', id: "notDistributed", minWidth: 100, width: 100, maxWidth: 100 },
        { Header: 'В резерве', accessor: 'attributes.reserved', id: "reserved", minWidth: 100, width: 100, maxWidth: 100 },
    ]);
    const [rowDataLoading, setRowDataLoading] = React.useState<boolean>(true);
    const [productsRequestError, setProductsRequestError] = React.useState<string>('');
    const [totalProducts, setTotalProducts] = React.useState<number>(0);
    const [tableSettings, setTableSettings] = React.useState<ITableSettingsLegacy>({
        limit: rowsPerPageBasedOnViewport() ?? 20,
        page: 1,
        filterBy: { data: { combinator: 'and', not: false, rules: [] } },
        sortBy: {},
        fields: []
    });
    const [tableSettingsLoaded, setTableSettingsLoaded] = React.useState<boolean>(false);
    let {
        rowData,
        idFilter,
        skuFilter,
        barcodeFilter,
        sizeFilter,
        nameFilter,
        brandFilter,
        notesFilter,
    } = useAppSelector((state) => ({
        rowData: state.stocksExport.rowData,
        idFilter: state.stocksExport.idFilter,
        skuFilter: state.stocksExport.skuFilter,
        barcodeFilter: state.stocksExport.barcodeFilter,
        sizeFilter: state.stocksExport.sizeFilter,
        nameFilter: state.stocksExport.nameFilter,
        brandFilter: state.stocksExport.brandFilter,
        notesFilter: state.stocksExport.notesFilter,
    }));
    let [isFirstRender, setIsFirstRender] = useState<boolean>(true);
    let [isManualExportAvailable, setIsManualExportAvailable] = useState<boolean>(false);
    const isStocksExportFormModalOpen = useAppSelector((state) => state.stocksExport.isStocksExportFormModalOpen);

    const toOpenStocksExportFormModal = () => {
        store.dispatch(setIsStocksExportFormModalOpen(true));
    };

    const toCloseStocksExportFormModal = (e: UIEvent) => {
        e.stopPropagation();
        store.dispatch(setIsStocksExportFormModalOpen(false));
    };

    const toCloseStocksExportFormModalOnClickOutside = (e: UIEvent) => {
        e.stopPropagation();
        store.dispatch(setIsStocksExportFormModalOpen(false));
    };

    const contentRef = useRef<HTMLDivElement>(null);

    const rowsDataFormatter = (rows: any[]) => {
        let arrOfRows: any[] = [];
        rows.forEach((row: any) => {
            const attrs: {[key: string]: any} = {
                id: row.id,
                photo: row.attributes?.find((a: {alias: string}) => a.alias === 'photo')?.value,
                name: row.attributes?.find((a: {alias: string}) => a.alias === 'name')?.value,
                sku: row.attributes?.find((a: {alias: string}) => a.alias === 'sku')?.value,
                barcode: row.attributes?.find((a: {alias: string}) => a.alias === 'barcode')?.value,
                size: row.attributes?.find((a: {alias: string}) => a.alias === 'size')?.value,
                brand: row.attributes?.find((a: {alias: string}) => a.alias === 'brand')?.value,
                notes: (row.attributes?.find((a: {alias: string}) => a.alias === 'notes')?.value || '').replace(/\r\n/ig," "),
                availableForPurchase: row.availableForPurchase,
                reserved: row.reserved,
                notDistributed: row.notDistributedStocks,
                isAutoUpdating: true
            }

            for (const whID in row.distributedAvailableStocksByWarehouses) {
                const rQ = row.distributedAvailableStocksByWarehouses[whID]?.appliedReserves
                attrs[`wh_${whID}`] = <div>
                    {rQ > 0 && <span className={`text-gray-400 text-xs`}><span title={`Зарезервировано`} className={`cursor-pointer`}>{row.distributedAvailableStocksByWarehouses[whID]?.appliedReserves}</span><span className={`mx-1`}>/</span></span>}
                    <span title={`К отправке`} className={`cursor-pointer`}>{row.distributedAvailableStocksByWarehouses[whID]?.quantity}</span>
                </div>
            }

            arrOfRows.push({
                id: row.id,
                attributes: attrs
            })
        })
        return arrOfRows;
    }

    // Костыль чтобы запрос не выполнялся на каждый рендер
    let isChangedTableSettings = useHasChanged(tableSettings);
    let isChangedIsNeedUpdateData = useHasChanged(isNeedUpdateData);
    useEffect(() => {
        if (!tableSettingsLoaded) return;

        // Костыль чтобы запрос не выполнялся на каждый рендер
        if (isFirstRender) {
            isChangedTableSettings = false
            isChangedIsNeedUpdateData = false
        }

        if (sellerId && (isFirstRender || isChangedTableSettings || (isChangedIsNeedUpdateData && isNeedUpdateData))) {
            console.log('isChangedIsNeedUpdateData', isChangedIsNeedUpdateData)
            console.log('isNeedUpdateData', isNeedUpdateData)
            updateData(sellerId)
        }

        // Костыль чтобы запрос не выполнялся на каждый рендер
        setIsFirstRender(false)
        isFirstRender = false
    }, [tableSettings, isNeedUpdateData, tableSettingsLoaded]);

    const initialRequestData: IProductsRequest = {
        limit: tableSettings.limit,
        page: tableSettings.page,
        sellerID: sellerId,
        withStocks: true,
        withStocksSum: true,
        channels: ["system"],
        filter: tableSettings.filterBy,
        ...(tableSettings.sortBy?.columnHeader && {
            order: { field: tableSettings.sortBy.columnHeader, direction: tableSettings.sortBy.sortIndex ?? 'asc' }
        }),
    }

    useEffect(() => {

        // prepare products
        // uuid закостылен для того чтобы после сериализации фильтра по нему можно было получить хэш и использовать как ключ кэша
        let rules: Array<IFilterOption> = [];
        if (idFilter) {
            rules.push({
                combinator: "",
                rules: [],
                "id": "3f4af2e1-6b4b-4268-8f92-3b945c8235b6",
                "field": "id",
                "operator": "=",
                "value": idFilter.toString()
            })
        }
        if (skuFilter) {
            rules.push({
                combinator: "",
                rules: [],
                "id": "3f4af2e1-6b4b-4268-8f92-3b945c8235b6",
                "field": "sku",
                "operator": "contains",
                "value": skuFilter
            })
        }
        if (barcodeFilter) {
            rules.push({
                combinator: "",
                rules: [],
                "id": "3f4af2e1-6b4b-4268-8f92-3b945c8235b6",
                "field": "barcode",
                "operator": "contains",
                "value": barcodeFilter
            })
        }
        if (sizeFilter) {
            rules.push({
                combinator: "",
                rules: [],
                "id": "3f4af2e1-6b4b-4268-8f92-3b945c8235b6",
                "field": "size",
                "operator": "contains",
                "value": sizeFilter
            })
        }
        if (nameFilter) {
            rules.push({
                combinator: "",
                rules: [],
                "id": "3f4af2e1-6b4b-4268-8f92-3b945c8235b6",
                "field": "name",
                "operator": "contains",
                "value": nameFilter
            })
        }
        if (brandFilter) {
            rules.push({
                combinator: "",
                rules: [],
                "id": "3f4af2e1-6b4b-4268-8f92-3b945c8235b6",
                "field": "brand",
                "operator": "contains",
                "value": brandFilter
            })
        }
        if (notesFilter) {
            rules.push({
                combinator: "",
                rules: [],
                "id": "3f4af2e1-6b4b-4268-8f92-3b945c8235b6",
                "field": "notes",
                "operator": "contains",
                "value": notesFilter
            })
        }

        setTableSettings((prev: ITableSettingsLegacy) => {
            return {
                ...prev,
                filterBy: { data: { combinator: 'and', not: false, rules: rules } },
            };
        });

    }, [idFilter, skuFilter, barcodeFilter, sizeFilter, nameFilter, brandFilter, notesFilter]);

    const latestRequest = useLatestRequest();

    function updateData(sellerId: number) {
        const requestId = latestRequest.begin();
        setRowDataLoading(true)

        // prepare stocks
        stocksExportService.getDistributedStocksForSeller(initialRequestData)
            .then(async (res) => {
                if (!latestRequest.isLatest(requestId)) return;

                const rowData = rowsDataFormatter(res.data.data.items)
                dispatch(setRowData([...rowData]))

                // Prepare warehouses export rules
                const setting = (await stocksExportService.getSettingsForSeller(sellerId))?.data?.data
                const rules = setting?.rulesForWarehouses || [];
                const rulesByWh:{[key: string]: IStocksExportRuleForWarehouse} = {}
                rules.forEach((r: IStocksExportRuleForWarehouse) => {
                    rulesByWh[r.warehouseID] = r
                    if (r.isEnableAutoExport) {
                        setIsManualExportAvailable(true)
                    }
                })

                if (!setting.isEnabledAutoExport) {
                    setIsManualExportAvailable(false)
                }

                // prepare warehouses
                warehousesService.getWarehousesForSeller(sellerId)
                    .then(
                        (res) => {
                            if (res.data.success) {
                                // @ts-ignore
                                function customSort(a: any, s: any) {
                                    return a.sort(function (x1: any, x2: any) {
                                        let i1 = s.indexOf(x1.type),
                                            i2 = s.indexOf(x2.type);
                                        return i1 < 0 ? 1 : i2 < 0 ? -1 : i1 - i2;
                                    })
                                }

                                let whs = customSort(res.data.data, ['fbs', 'supplier', 'external_fbs', 'external_fbo']);

                                const whsIdsForDistribute: string[] = []

                                whs.forEach((wh: { name: string, uuid: string, status: string, type: string, externalService: any, externalID: any, isEnabledAutoImportStocksByDS: boolean, isForSale: boolean, isEnableExport: boolean }) => {

                                    // Filter
                                    if (!wh.isEnableExport || wh.status !== 'active') {
                                        return;
                                    }

                                    // Set columns
                                    const colId: string = `wh_${wh.uuid}`
                                    const columnAlreadyAdd = columns.find(({id}) => id === colId)
                                    const isExternalWarehouse = [WarehouseType.ExternalWarehouseFBO.toString(), WarehouseType.ExternalWarehouseFBS.toString()].includes(wh.type)

                                    if (!columnAlreadyAdd) {
                                        // @ts-ignore
                                        columns.push({
                                            Header: wh.name + (isExternalWarehouse ? `, ${wh.externalID}` : ''),
                                            Footer:
                                                <div className="text-c-light-black font-normal text-left">
                                                    <div className="flex justify-end items-center">
                                                        <span>{t(warehouseTypeLabels[wh.type ?? ''])}</span>
                                                    </div>
                                                </div>,
                                            Cell:
                                                <>
                                                    {wh.externalService !== '' &&
                                                        <div className={"ml-2 float-right w-[35%]"}>
                                                            <Icon width={30} height={30}
                                                                  marketSource={wh.externalService}/>
                                                        </div>
                                                    }
                                                </>
                                            ,
                                            accessor: `attributes.wh_${wh.uuid}`,
                                            id: colId,
                                        })
                                    }
                                })

                                setColumns([...columns])
                            }
                        },
                        (err) => {
                            setProductsRequestError(err.response.data.errors[0] || 'Undefined error');
                        }
                    )
                    .catch((err) => {
                        setProductsRequestError(err.message);
                    })
                ;

                //@ts-ignore
                setTotalProducts(res.data.data.total)
                setRowDataLoading(false)
                disableIsNeedUpdateData(10)

            })
            .catch((err) => {
                if (!latestRequest.isLatest(requestId)) return;
                setProductsRequestError(err.message);
                setRowDataLoading(false);
                disableIsNeedUpdateData(10)
            })
            .finally(() => { if (latestRequest.isLatest(requestId)) setRowDataLoading(false) })
    }

    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const [selectAllMode, setSelectAllMode] = useState(false);
    const { actionsVisible, editingMode, setEditingMode, toggleActions } = useSelectedActions(selectedRows.length > 0 || selectAllMode);

    const getSelectedIds = async (): Promise<number[]> => {
        if (selectAllMode && sellerId) {
            try {
                const res = await stocksExportService.getIdsByFilter(sellerId, tableSettings.filterBy);
                return res.data?.data?.ids ?? [];
            } catch {
                return [];
            }
        }
        return selectedRows.map((r: any) => r.original.id);
    };
    const [sidebarCollapsed, setSidebarCollapsed] = useFiltersSidebarCollapsed();

    const exportSidebarSections: FilterSidebarSection[] = [
        {
            id: 'status',
            summaryItem: { key: 'all', label: 'Все' },
            defaultOpen: true,
            items: [
                { key: 'inStock', label: 'В наличии' },
            ],
        },
        {
            id: 'tags',
            title: 'Теги',
            addButton: true,
            defaultOpen: false,
            items: [],
            hidden: true,
        },
    ];
    const [exportActiveFilter, setExportActiveFilter] = useState<string>('all');

    // @ts-ignore
    return (
        <div className="flex flex-col h-full">
            <Actions noBorder noMargin scrollable>
                <div className="flex items-center justify-between w-full">
                    <div className="flex items-center">
                        <button
                            onClick={() => setSidebarCollapsed(v => !v)}
                            className="flex p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-[#1e293b] rounded transition-colors flex-shrink-0"
                            title={sidebarCollapsed ? 'Показать фильтры' : 'Скрыть фильтры'}
                        >
                            {sidebarCollapsed
                                ? <FilterFunnelIcon width={20} height={20} style={{ fill: 'currentColor' }} />
                                : <HideMenuIcon width={20} height={20} style={{ fill: 'currentColor' }} />
                            }
                        </button>
                        <Tabs selected={`stocks-export`}></Tabs>

                        <div className="ml-3 flex items-center gap-1">
                            <ExportStocksToWarehouses disabled={!isManualExportAvailable}/>
                            <RecalcAndExportStocksToWarehouses disabled={!isManualExportAvailable}/>
                            <OpenStocksExportForm toOpenStocksExportFormModal={toOpenStocksExportFormModal}></OpenStocksExportForm>
                        </div>
                        {actionsVisible && (selectedRows.length > 0 || selectAllMode) && (
                            <SelectedActionsButton
                                label={selectAllMode ? `С выбранными (все ${totalProducts})` : `С выбранными (${selectedRows.length})`}
                                editingMode={editingMode}
                                onToggleEditing={() => setEditingMode(!editingMode)}
                                onAddToFilter={async () => {
                                    const ids = await getSelectedIds();
                                    if (ids.length > 0) {
                                        // @ts-ignore
                                        dispatch(setIdFilter(ids[0]));
                                    }
                                }}
                            />
                        )}
                    </div>
                </div>
            </Actions>

            <div className="flex flex-1 min-h-0">
                {!sidebarCollapsed && (
                    <div className="hidden lg:flex">
                        <StocksFilterSidebar
                            sections={exportSidebarSections}
                            activeFilter={exportActiveFilter}
                            onChange={setExportActiveFilter}
                        />
                    </div>
                )}
                <div className="flex-1 min-w-0 overflow-hidden">
                    <Table
                        columns={columns}
                        tableSettingsLoaded={tableSettingsLoaded}
                        totalProducts={totalProducts}
                        tableSettings={tableSettings}
                        setTableSettings={setTableSettings}
                        data={rowData}
                        rowDataLoading={rowDataLoading}
                        productsRequestError={productsRequestError}
                        isNeedUpdateData={isNeedUpdateData}
                        setSelectedRows={setSelectedRows}
                        setIdFilter={setIdFilter}
                        setSkuFilter={setSkuFilter}
                        setBarcodeFilter={setBarcodeFilter}
                        setSizeFilter={setSizeFilter}
                        setNameFilter={setNameFilter}
                        setBrandFilter={setBrandFilter}
                        setNotesFilter={setNotesFilter}
                        idFilter={idFilter}
                        skuFilter={skuFilter}
                        barcodeFilter={barcodeFilter}
                        sizeFilter={sizeFilter}
                        nameFilter={nameFilter}
                        brandFilter={brandFilter}
                        notesFilter={notesFilter}
                        editRowDataElement={editRowDataElement}
                        onLayoutReady={() => setTableSettingsLoaded(true)}
                        onSelectAllModeChange={setSelectAllMode}
                        editingMode={editingMode}
                        setEditingMode={setEditingMode}
                        onSelectedActionsToggle={toggleActions}
                    />
                </div>
            </div>

            {/* Mobile filter drawer */}
            {/* @ts-ignore */}
            <Transition
                className="fixed inset-0 bg-gray-900 bg-opacity-30 z-[70] lg:hidden"
                show={!sidebarCollapsed}
                enter="transition ease-out duration-200"
                enterStart="opacity-0"
                enterEnd="opacity-100"
                leave="transition ease-out duration-200"
                leaveStart="opacity-100"
                leaveEnd="opacity-0"
                aria-hidden="true"
                appear={undefined}
                onClick={() => setSidebarCollapsed(true)}
            />
            {/* @ts-ignore */}
            <Transition
                className="fixed inset-y-0 left-0 z-[70] lg:hidden w-[280px] max-w-[85vw] flex flex-col bg-[#d3ddec] dark:bg-[#1e293b] shadow-xl"
                show={!sidebarCollapsed}
                enter="transition ease-in-out duration-200 transform"
                enterStart="-translate-x-full"
                enterEnd="translate-x-0"
                leave="transition ease-in-out duration-200 transform"
                leaveStart="translate-x-0"
                leaveEnd="-translate-x-full"
                appear={undefined}
            >
                <div className="flex items-center justify-between px-3 py-3 border-b border-[#bccce3] dark:border-slate-700 shrink-0">
                    <span className="text-sm font-bold text-[#1E293B] dark:text-slate-100">Фильтры</span>
                    <button
                        className="text-gray-400 hover:text-gray-500 dark:text-slate-400 dark:hover:text-slate-300 bg-[#F1F5F9] dark:bg-slate-700 p-2 rounded-full cursor-pointer transition-colors"
                        onClick={() => setSidebarCollapsed(true)}
                    >
                        <svg className="w-3 h-3 fill-current">
                            <path d="M7.95 6.536l4.242-4.243a1 1 0 111.415 1.414L9.364 7.95l4.243 4.242a1 1 0 11-1.415 1.415L7.95 9.364l-4.243 4.243a1 1 0 01-1.414-1.415L6.536 7.95 2.293 3.707a1 1 0 011.414-1.414L7.95 6.536z" />
                        </svg>
                    </button>
                </div>
                <div className="flex-1 overflow-auto">
                    <StocksFilterSidebar
                        sections={exportSidebarSections}
                        activeFilter={exportActiveFilter}
                        onChange={(key) => {
                            setExportActiveFilter(key);
                            setSidebarCollapsed(true);
                        }}
                        fluid
                    />
                </div>
            </Transition>
            <SlidingContainer
                //@ts-ignore
                title="Настройка отправки и синхронизации остатков"
                id="stocks-export-form-modal"
                modalOpen={isStocksExportFormModalOpen}
                toCloseModal={toCloseStocksExportFormModal}
                toCloseModalOnClickOutside={toCloseStocksExportFormModalOnClickOutside}
                modalContent={contentRef}
                minWidth={760}
            >
                <FormStocksExport></FormStocksExport>
            </SlidingContainer>
        </div>
    );
};

export default StocksExport;
